import type {Map} from 'leaflet';
import L from 'leaflet';

import settings from '@/config/settings';
import type {ISettingsMapTile} from '@/config/types';

const applyRetinaSuffix = (url: string, suffix: string): string => {
  const lastDot = url.lastIndexOf('.');
  if (lastDot === -1) return url + suffix;

  return url.slice(0, lastDot) + suffix + url.slice(lastDot);
};

const resolveTileUrl = (tile: ISettingsMapTile): string => {
  if (
    tile.retinaSuffix &&
    typeof window !== 'undefined' &&
    window.devicePixelRatio > 1
  ) {
    return applyRetinaSuffix(tile.url, tile.retinaSuffix);
  }

  return tile.url;
};

const DEFAULT_TILES: ISettingsMapTile[] = [
  {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    minZoom: 3,
    maxZoom: 13,
    maxZoomNative: 13,
  },
];

export const createTileLayers = (
  map: Map,
  tiles?: ISettingsMapTile[],
  pane?: string,
): L.TileLayer[] => {
  const resolved = tiles && tiles.length ? tiles : DEFAULT_TILES;

  return resolved.map((tile) => {
    const url = resolveTileUrl(tile);

    return L.tileLayer(url, {
      minZoom: tile.minZoom,
      maxZoom: tile.maxZoom,
      maxNativeZoom: tile.maxZoomNative,
      tms: tile.tms ?? false,
      updateWhenZooming: false,
      zIndex: 0,
      pane: pane,
    } as L.TileLayerOptions);
  });
};

export const createBaseTileLayers = (
  map: Map,
  tiles?: ISettingsMapTile[],
): void => {
  const layers = createTileLayers(map, tiles);
  layers.forEach((layer) => layer.addTo(map));
};

export const getTilePresets = () => settings.SERVICES.MAP.TILE_PRESETS;

export const getDefaultTilePresetIndex = (): number => {
  const presets = getTilePresets();
  const idx = presets.findIndex((p) => p.default);
  return idx >= 0 ? idx : 0;
};

export const getTilePresetTiles = (index: number): ISettingsMapTile[] => {
  const presets = getTilePresets();
  if (index < 0 || index >= presets.length) return DEFAULT_TILES;
  return presets[index].tiles;
};
